// mapUtils.js
import './style.css';
import 'bootstrap';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { createBoundaryGeoJSON } from './baseFeatureIdentity.js';
import { bbox as bboxStrategy } from 'ol/loadingstrategy.js';
import { GEOSERVER_URL } from './config.js';
import { Style, Fill, Stroke} from 'ol/style.js';
import Select from 'ol/interaction/Select.js';
import { click } from 'ol/events/condition.js';
import { getMap } from './mapInstance.js';
import { loadLayerSourceForCurrentWindow } from './layerSourceLoading.js';
import GroupLayer from 'ol/layer/Group.js';
import { Draw } from 'ol/interaction.js';
import CircleStyle from 'ol/style/Circle.js';
import * as addressSearch from './addressSearch.js';




let selectionLayer;
let selectionSet = new Set();
const geojsonFormat = new GeoJSON();

const addressColor = '#0078FF';               // Blue
const addressFill = 'rgba(0,120,255,0.5)';   // Light blue
const selectionColor = '#FF33CC';                 // Magenta
const selectionFill = 'rgba(255,51,204,0.5)';     // Light magenta

/**
 * Updates the UI status text showing how many addresses are selected.
 */
function updateSelectionStatus() {
  try {
    const count = selectionSet.size;
    const label = count === 1 ? 'address' : 'addresses';
    setStatus(`${count} ${label} selected`);
  } catch (err) {
    console.warn('updateSelectionStatus: failed to update status text', err);
  }
}

export function getSelectionStyle() {
  return new Style({
    stroke: new Stroke({ color: selectionColor, width: 3 }),
    fill: new Fill({ color: selectionFill }),
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: selectionColor }),
      stroke: new Stroke({ color: '#FFFFFF', width: 2 })
    })
  });
}

export function getAddressSelectionStyle() {
  return new Style({
    stroke: new Stroke({ color: addressColor, width: 3 }),
    fill: new Fill({ color: addressFill }),
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: addressColor }),
      stroke: new Stroke({ color: '#FFFFFF', width: 2 })
    })
  });
}

export function getSelectionLayer() 
{

  const map = getMap();
  if (!map) {
    console.warn("Selection layer requested before map ready");
    return null;
  }


  if (!selectionLayer) {
    selectionLayer = new VectorLayer({
      source: new VectorSource(),
      zIndex: 9999,
      // respect feature-specific style (magenta toggle)
      style: feature => feature.getStyle() || getSelectionStyle()
    });

  }

  const layers = map.getLayers().getArray();
  if (!layers.includes(selectionLayer)) {
    map.addLayer(selectionLayer);
  }
  
  return selectionLayer;
}


/**
 * Create a dynamic OpenLayers VectorImageLayer from BaseLayerDTO
 */
export function createVectorLayer(layerDTO) {
  const source = new VectorSource({
    format: createBoundaryGeoJSON({}, layerDTO.typeName),
    strategy: bboxStrategy,
    url: extent =>
      `${GEOSERVER_URL}?` +
      `service=WFS&version=1.0.0&request=GetFeature&` +
      `typeName=${layerDTO.typeName}&` +
      `outputFormat=application/json&` +
      `srsName=EPSG:3857&` +
      `bbox=${extent.join(',')},EPSG:3857`
  });

  const range = normalizeLayerVisibilityRange(layerDTO.minResolution, layerDTO.maxResolution);
  const layerOptions = {
    title: layerDTO.title,
    source,
    style: new Style({
      fill: new Fill({ color: layerDTO.fillColor || 'rgba(0,0,255,0.2)' }),
      stroke: new Stroke({ color: layerDTO.strokeColor || '#0000FF', width: 1.2 })
    }),
    visible: layerDTO.visible ?? false
  };

  if (range.mode === 'zoom') {
    layerOptions.minZoom = range.minZoom;
    layerOptions.maxZoom = range.maxZoom;
  } else {
    layerOptions.minResolution = range.minResolution;
    layerOptions.maxResolution = range.maxResolution;
  }

  const layer = new VectorLayer(layerOptions);
  layer.set('configuredMinResolution', layerDTO.minResolution);
  layer.set('configuredMaxResolution', layerDTO.maxResolution);
  layer.set('normalizedVisibilityRange', range);

  return layer;
}

/**
 * Resolution / zoom conversion helpers
 */
export function resolutionToZoom(resolution) {
  const baseResolution = 156543.03;
  return Math.log2(baseResolution / resolution);
}

export function zoomToResolution(zoom) {
  const baseResolution = 156543.03;
  return baseResolution / Math.pow(2, zoom);
}

export function normalizeLayerVisibilityRange(minValue, maxValue) {
  const min = Number(minValue);
  const max = Number(maxValue);
  const minFinite = Number.isFinite(min);
  const maxFinite = Number.isFinite(max);

  if (!minFinite && !maxFinite) {
    return {
      mode: 'all',
      minResolution: 0,
      maxResolution: Infinity,
      minZoom: -Infinity,
      maxZoom: Infinity
    };
  }

  const looksLikeZoomRange =
    minFinite &&
    maxFinite &&
    min >= 0 &&
    max >= 0 &&
    min <= 60 &&
    max <= 60 &&
    min > max;

  if (looksLikeZoomRange) {
    return {
      mode: 'zoom',
      minResolution: 0,
      maxResolution: Infinity,
      minZoom: Math.min(min, max),
      maxZoom: Math.max(min, max)
    };
  }

  const lowerResolution = minFinite && maxFinite
    ? Math.min(min, max)
    : minFinite
      ? min
      : 0;
  const upperResolution = minFinite && maxFinite
    ? Math.max(min, max)
    : maxFinite
      ? max
      : Infinity;

  return {
    mode: 'resolution',
    minResolution: Number.isFinite(lowerResolution) ? Math.max(0, lowerResolution) : 0,
    maxResolution: Number.isFinite(upperResolution) ? Math.max(0, upperResolution) : Infinity,
    minZoom: -Infinity,
    maxZoom: Infinity
  };
}

export function getLayerVisibilityRange(layer) {
  const existing = layer?.get?.('normalizedVisibilityRange');
  if (existing) return existing;

  return normalizeLayerVisibilityRange(
    layer?.get?.('configuredMinResolution') ?? layer?.getMinResolution?.() ?? layer?.get?.('minResolution'),
    layer?.get?.('configuredMaxResolution') ?? layer?.getMaxResolution?.() ?? layer?.get?.('maxResolution')
  );
}

export function isViewInLayerVisibilityRange(layer, resolution, zoom) {
  const range = getLayerVisibilityRange(layer);
  const resolutionOk =
    !Number.isFinite(resolution) ||
    (resolution >= range.minResolution && resolution <= range.maxResolution);
  const zoomOk = isZoomWithinLayerRange(zoom, range.minZoom, range.maxZoom);

  return resolutionOk && zoomOk;
}

function resolutionToScale(resolution, dpi = 96) {
  const inchesPerMeter = 39.37;
  return resolution * inchesPerMeter * dpi;
}

/**
 * Update layer visibility depending on zoom range
 */
export function updateLayerVisibilityByZoom(targetGroup) {

  const map = getMap();
  if (!map || !targetGroup) return;

  const resolution = map.getView().getResolution();
  if (!Number.isFinite(resolution)) return;
  const zoom = map.getView().getZoom?.();

  const extent = map.getSize?.()
    ? map.getView().calculateExtent(map.getSize())
    : null;
  const projection = map.getView().getProjection?.();

  targetGroup.getLayers().forEach(layer => {
    const hasLayerRight = !layer.get('disabledByRights');
    const enabledByBackend = layer.get('backendVisible') !== false;
    const enabledByUser = !!layer.getVisible();
    const isVisibleAtCurrentZoom =
      hasLayerRight &&
      enabledByBackend &&
      enabledByUser &&
      isViewInLayerVisibilityRange(layer, resolution, zoom);

    layer.set('visibleAtCurrentZoom', isVisibleAtCurrentZoom);

    if (isVisibleAtCurrentZoom) {
      loadLayerSourceForCurrentWindow(layer, extent, resolution, projection, map.getView());
    }
  });

  document.dispatchEvent(new CustomEvent('base-layer-zoom-visibility-updated'));
}

function isZoomWithinLayerRange(zoom, minZoom, maxZoom) {
  return !Number.isFinite(zoom) || (zoom >= minZoom && zoom <= maxZoom);
}

export function calculateScale(map) {
  if (!map || !map.getView) return null;

  const view = map.getView();
  const resolution = view.getResolution();
  const projection = view.getProjection();
  if (!resolution || !projection) return null;

  const mpu = projection.getMetersPerUnit() || 1;
  const dpi = 25.4 / 0.28; // 90.714 DPI per OGC standard
  const inchesPerMeter = 39.37;

  const scale = resolution * mpu * inchesPerMeter * dpi;
  return Math.round(scale);
}

export function updateScaleInfo(map) {
  const scaleEl = document.getElementById('scale-text');
  if (!scaleEl) return;

  const scale = calculateScale(map);
  scaleEl.textContent = Number.isFinite(scale) && scale > 0
    ? `1:${scale.toLocaleString()}`
    : '-';
}

export function resolutionFromScaleDenominator(scale, projection = null) {
  const denominator = Number(scale);
  if (!Number.isFinite(denominator) || denominator <= 0) return null;

  const mpu = projection?.getMetersPerUnit?.() || 1;
  const dpi = 25.4 / 0.28; // 90.714 DPI per OGC standard
  const inchesPerMeter = 39.37;
  return denominator / (mpu * inchesPerMeter * dpi);
}

export function zoomFromScaleDenominator(scale, projection = null) {
  const resolution = resolutionFromScaleDenominator(scale, projection);
  return Number.isFinite(resolution) && resolution > 0
    ? resolutionToZoom(resolution)
    : null;
}


/**
 * Highlight features returned from backend or GeoServer.
 * Ensures consistent projection + style.
 */
export function highlightFeaturesFromResult(result, clearSelection = true, dataProjection = 'EPSG:4326') {

    const map = getMap();
  if (!map) return;

  if (!result || !Array.isArray(result.features)) {
    console.warn('highlightFeaturesFromResult: unexpected input', result);
    return;
  }

  const format = new GeoJSON();
  const features = format.readFeatures(result, {
    dataProjection,
    featureProjection: map.getView().getProjection()
  });

  const selLayer = getSelectionLayer();
  const selSrc = selLayer.getSource();

  if (clearSelection) {
    selSrc.clear();
    selectionSet.clear();
  }

  const styledFeatures = features.map(f => {
    const clone = f.clone();
    clone.setStyle(getSelectionStyle()); // 🔵 blue for areas
    selectionSet.add(clone);
    return clone;
  });

  selSrc.addFeatures(styledFeatures);
  updateSelectionStatus();
  map.render();
}


/**
 * Toggle address feature selection (magenta for active)
 */
export function toggleSelection(feature) {

    const map = getMap();
  if (!map) return;

  if (!(feature && typeof feature.getGeometry === 'function')) {
    console.error("toggleSelection expected an OL Feature, got:", feature);
    return;
  }

  const selLayer = getSelectionLayer();
  const selSrc = selLayer.getSource();

  if (!selSrc) return;

  // Compute a stable feature ID
  const fid =
    (typeof feature.getId === 'function' && feature.getId()) ||
    feature.get('id') ||
    JSON.stringify(feature.getProperties());

  // Find if feature already selected
  let matchedFeature = null;
  for (let f of selectionSet) {
    const otherId =
      (typeof f.getId === 'function' && f.getId()) ||
      f.get('id') ||
      JSON.stringify(f.getProperties());
    if (otherId === fid) {
      matchedFeature = f;
      break;
    }
  }

  if (matchedFeature) {
    // Deselect — remove from map and selection set
    selSrc.removeFeature(matchedFeature);
    selectionSet.delete(matchedFeature);
  } else {
    // Select — clone, apply magenta style
    const clone = feature.clone();
    clone.setId(fid);
    clone.setStyle(getAddressSelectionStyle());
    selSrc.addFeature(clone);
    selectionSet.add(clone);
    
  }

  updateSelectionStatus();
  map.render();
}

/**
 * Returns the current count of selected features in the selection layer.
 */
export function getSelectionCount() {
  const selLayer = getSelectionLayer && getSelectionLayer();
  if (!selLayer) return 0;

  const src = selLayer.getSource ? selLayer.getSource() : null;
  return src ? src.getFeatures().length : 0;
}



export function replaceSelectionSet(features) {

  const map = getMap();
if (!map) return;

  const selLayer = getSelectionLayer();
  const selSource = selLayer.getSource();
  selSource.clear();
  selectionSet.clear();

  // Transform all to map projection & add to unified layer
  const mapProj = map.getView().getProjection();
  const styledFeatures = features.map(f => {
    const clone = f.clone();
    clone.getGeometry().transform('EPSG:3857', mapProj); // if GeoServer WFS
    selectionSet.add(clone);
    return clone;
  });

  selSource.addFeatures(styledFeatures);
  updateSelectionStatus();
  map.render();
}

export function clearSelection() {

  console.warn("WRONG clearSelection CALLED for AddressSearch");

  const map = getMap();
  if (!map) return;

  const layer = getSelectionLayer();
  if (!layer) return;

  const source = layer.getSource();

  // Remove all features from the layer
  source.clear(true);

  // Reset internal selection storage
  selectionSet.clear();

  // Force redraw
  layer.changed();
  map.renderSync();

  updateSelectionStatus();
  addressSearch.clearSelectionSet();
}

export function clearSearchSelectionOnly() {
  const map = getMap();
  if (!map) return;

  const layer = getSelectionLayer();
  if (!layer) return;

  layer.getSource().clear(true);
  selectionSet.clear();
  layer.changed();
  map.renderSync();
  updateSelectionStatus();
}

/**
 * Get selected addresses (non-geometry props) from selection layer
 */
export function getSelectedAddresses() {
  return getSelectedAddresses_map();
}

/**
 * Extract selected addresses from selection layer
 */
export function getSelectedAddresses_map() {
  const selLayer = getSelectionLayer();
  const features = selLayer.getSource().getFeatures();
  return features.map(f => {
    const props = { ...f.getProperties() };
    delete props.geometry;
    return props;
  });
}

/**
 * Enable click selection on a given layer
 */
let selectInteraction = null;

export function enableSelectOnLayer(targetLayer) {

  const map = getMap();
  if (!map) return;

  if (selectInteraction) {
    map.removeInteraction(selectInteraction);
  }

  selectInteraction = new Select({
    condition: click,
    layers: [targetLayer],
    style: new Style({
      stroke: new Stroke({ color: 'magenta', width: 4 }),
      fill: new Fill({ color: 'rgba(255, 0, 255, 0.3)' })
    })
  });

  map.addInteraction(selectInteraction);

  selectInteraction.on('select', (e) => {
    console.log("Selected features:", e.selected);
  });
}

/**
 * Wait for vector sources to load features
 */
export async function waitForAllFeaturesLoaded(map, timeout = 20000) {

  const vectorSources = [];

  map.getLayers().forEach(layer => {
    const source = layer.getSource?.();
    if (source?.getFeatures && typeof source.on === 'function') {
      vectorSources.push(source);
    }
  });

  const loadPromises = vectorSources.map(source => {
    return new Promise(resolve => {
      if (source.getFeatures().length > 0) {
        resolve();
      } else {
        source.once('featuresloadend', () => {
          resolve();
        });
      }
    });
  });

  const timeoutPromise = new Promise(resolve => {
    setTimeout(resolve, timeout);
  });

  return Promise.race([Promise.all(loadPromises), timeoutPromise]);
}


/*++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
 * Makes a panel draggable using its header.
 * @param {string} panelId - The ID of the panel container.
 * @param {string} headerId - The ID of the panel's header (drag handle).
 ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++*/
// mapUtils.js

export function makeDraggable(panel, handle) {
  if (!panel || !handle) return;

  if (panel.__sevennDragCleanup) {
    panel.__sevennDragCleanup();
  }

  let offsetX = 0, offsetY = 0, isDown = false, activePointerId = null;

  handle.style.cursor = 'move';
  handle.style.touchAction = 'none';

  const isInteractiveTarget = (target) =>
    target?.closest?.('button, input, select, textarea, a, label, [data-no-drag]');

  const getPoint = (e) => e.touches?.[0] || e.changedTouches?.[0] || e;

  const onDragStart = (e) => {
    if ((e.button ?? 0) !== 0 || isInteractiveTarget(e.target)) return;
    isDown = true;
    activePointerId = e.pointerId ?? null;
    const rect = panel.getBoundingClientRect();
    const point = getPoint(e);
    offsetX = point.clientX - rect.left;
    offsetY = point.clientY - rect.top;
    panel.style.setProperty('left', `${rect.left}px`, 'important');
    panel.style.setProperty('top', `${rect.top}px`, 'important');
    panel.style.setProperty('right', 'auto', 'important');
    panel.style.setProperty('bottom', 'auto', 'important');
    panel.style.transition = 'none';
    panel.classList.add('is-dragging');
    handle.setPointerCapture?.(activePointerId);
    e.preventDefault();
  };

  const onDragMove = (e) => {
    if (!isDown) return;
    if (activePointerId !== null && e.pointerId !== activePointerId) return;
    const point = getPoint(e);
    const width = panel.offsetWidth;
    const height = panel.offsetHeight;
    const margin = 8;
    const left = Math.min(Math.max(margin, point.clientX - offsetX), Math.max(margin, window.innerWidth - width - margin));
    const top = Math.min(Math.max(margin, point.clientY - offsetY), Math.max(margin, window.innerHeight - height - margin));

    panel.style.setProperty('left', `${left}px`, 'important');
    panel.style.setProperty('top', `${top}px`, 'important');
    panel.style.setProperty('right', 'auto', 'important');
    panel.style.setProperty('bottom', 'auto', 'important');
    e.preventDefault();
  };

  const onDragEnd = (e) => {
    if (activePointerId !== null && e?.pointerId !== undefined && e.pointerId !== activePointerId) return;
    isDown = false;
    activePointerId = null;
    panel.style.transition = '';
    panel.classList.remove('is-dragging');
  };

  handle.addEventListener('pointerdown', onDragStart);
  document.addEventListener('pointermove', onDragMove, { passive: false });
  document.addEventListener('pointerup', onDragEnd);
  document.addEventListener('pointercancel', onDragEnd);

  panel.__sevennDragCleanup = () => {
    handle.removeEventListener('pointerdown', onDragStart);
    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup', onDragEnd);
    document.removeEventListener('pointercancel', onDragEnd);
    delete panel.__sevennDragCleanup;
  };
}

// Draggable modal (same as panel, separate for clarity)
export function makeModalDraggable(modal, handle) {
  makeDraggable(modal, handle);
}

// Optional: utility to highlight panel header cursor
export function setHeaderCursor(handle) {
  if (handle) handle.style.cursor = 'move';
}


/**
 * Auto-hide bottom info panels (status/zoom) when user idle, and fade in on activity.
 */
let hideTimeout = null;
let isHidden = false;

export function enableStatusBarAutoHide() {
  const mapInfo = document.getElementById('map-info');
  if (!mapInfo) {
    console.warn('enableStatusBarAutoHide: #map-info not found');
    return;
  }

  let hideTimeout = null;
  let isHidden = false;

  const showBar = () => {
    if (isHidden) {
      mapInfo.style.opacity = 1;
      isHidden = false;
    }

    if (hideTimeout) clearTimeout(hideTimeout);
    hideTimeout = setTimeout(() => {
      const cmdText = document.getElementById('command-text')?.textContent || '';
      if (cmdText.toLowerCase().includes('select') || cmdText.toLowerCase().includes('draw')) return;

      mapInfo.style.opacity = 0;
      isHidden = true;
    }, 5000);
  };

  ['mousemove', 'mousedown', 'touchstart', 'keydown'].forEach(evt =>
    window.addEventListener(evt, showBar)
  );

  showBar();
}


/**
 * Keeps bottom info panels above OS UI and spaced from each other.
 */
export function adjustStatusBar() {
  const mapInfo = document.getElementById('map-info');
  
  if (!mapInfo) return;

  const safeInset = parseFloat(
    getComputedStyle(document.documentElement)
      .getPropertyValue('env(safe-area-inset-bottom)')
  ) || 0;

  const baseOffset = 15 + safeInset;   // for command/status (left)
  if (mapInfo) mapInfo.style.bottom = `${baseOffset}px`;
  
}



/**
 * Misc UI helpers
 */
export function setCommand(text) {
  const el = document.getElementById('command-text');
  if (el) el.textContent = text;
  adjustStatusBar();
}

export function setStatus(text) {
  const el = document.getElementById('status-text');
  if (el) el.textContent = text;
  adjustStatusBar();
}

export function setMessage(text) {
  const el = document.getElementById('message-text');
  if (el) el.textContent = text;
  adjustStatusBar();
}

let baseLegendUpdatesSuspendedUntil = 0;

export function suppressBaseLegendUpdates(durationMs = 0) {
  baseLegendUpdatesSuspendedUntil = Math.max(
    baseLegendUpdatesSuspendedUntil,
    Date.now() + durationMs
  );
}


export function setMapCursor(cursorStyle) {
  const mapElement = document.getElementById('map');
  if (!mapElement) return;

  // OpenLayers uses this element for pointer events
  const viewport = mapElement.querySelector('.ol-viewport') || mapElement;

  viewport.style.cursor = cursorStyle;
}


export function getAllLayers(layerGroupOrMap) {
  const layers = [];
  const collection = layerGroupOrMap.getLayers();
  if (!collection) return layers;

  collection.forEach(layer => {
    if (layer instanceof GroupLayer) {
      layers.push(...getAllLayers(layer)); // recurse into subgroups
    } else {
      layers.push(layer);
    }
  });

  return layers;
}

// Attach events once (not recursively)
window.addEventListener('resize', adjustStatusBar);
window.addEventListener('orientationchange', adjustStatusBar);
document.addEventListener('fullscreenchange', adjustStatusBar);
window.addEventListener('load', adjustStatusBar);

export function withMap(fn) {
  if (!map) {
    console.warn("Map not ready, skipping execution");
    return;
  }
  fn(map);
}

export function removeAllDrawInteractions() {
  const map = getMap();
  if (!map) return;

  map.getInteractions().forEach(i => {
    if (i instanceof Draw) {
      map.removeInteraction(i);
    }
  });
}

export function cancelActiveTools(reason = 'Cancelled', options = {}) {

  const map = getMap();
  if (!map) return;

  document.dispatchEvent(new CustomEvent('draw-summary-edit-cancel', {
    detail: { silentStatus: true }
  }));

  removeAllDrawInteractions ();

  // Optional: reset cursor
  const viewport = map.getTargetElement()?.querySelector('.ol-viewport');
  if (viewport) viewport.style.cursor = '';

  // Update status bar
  setCommand('Ready');
  setStatus(reason);
  if (!options.preserveMessage) {
    setMessage('-');
  }
}

function styleToLegendHTML(style) {
  if (!style) return '';

  const fill = style.getFill?.();
  const stroke = style.getStroke?.();
  const image = style.getImage?.();

  // Point (Circle)
  //if (image instanceof ol.style.Circle) {
  if (image instanceof CircleStyle) {
    const r = image.getRadius();
    const fc = image.getFill()?.getColor() || '#000';
    const sc = image.getStroke()?.getColor() || '#000';
    return `
      <span style="
        display:inline-block;
        width:${r * 2}px;
        height:${r * 2}px;
        border-radius:50%;
        background:${fc};
        border:2px solid ${sc};
      "></span>
    `;
  }

  // Polygon / Line
  const fillColor = fill?.getColor() || 'transparent';
  const strokeColor = stroke?.getColor() || '#000';

  return `
    <span style="
      display:inline-block;
      width:16px;
      height:12px;
      background:${fillColor};
      border:2px solid ${strokeColor};
    "></span>
  `;
}

function normalizeBackendStyle(styleValue) {
  if (!styleValue) return null;

  if (typeof styleValue === 'object') return styleValue;

  if (typeof styleValue !== 'string') return null;

  try {
    return JSON.parse(styleValue);
  } catch (err) {
    console.warn('Failed to parse backend legend style:', err);
    return null;
  }
}

function backendStyleToLegendHTML(styleValue) {
  const style = normalizeBackendStyle(styleValue);
  if (!style) return '';

  const image = style.image || style.circle;
  if (image) {
    const radius = Number(image.radius) || 5;
    const fillColor = image.fill?.color || image.fillColor || image.color || '#000';
    const strokeColor = image.stroke?.color || image.strokeColor || '#000';
    const strokeWidth = Number(image.stroke?.width ?? image.strokeWidth ?? 2);

    return `
      <span style="
        display:inline-block;
        width:${radius * 2}px;
        height:${radius * 2}px;
        border-radius:50%;
        background:${fillColor};
        border:${strokeWidth}px solid ${strokeColor};
      "></span>
    `;
  }

  const fillColor = style.fill?.color || style.fillColor || 'transparent';
  const strokeColor = style.stroke?.color || style.strokeColor || style.color || '#000';
  const strokeWidth = Number(style.stroke?.width ?? style.strokeWidth ?? 2);

  return `
    <span style="
      display:inline-block;
      width:16px;
      height:12px;
      background:${fillColor};
      border:${strokeWidth}px solid ${strokeColor};
    "></span>
  `;
}

export function updateBaseLegendMessage(baseGroup) {

  if (Date.now() < baseLegendUpdatesSuspendedUntil) return;

  const map = getMap();
  if (!map || !baseGroup) return;

  const msgEl = document.getElementById('message-text');
  if (!msgEl || !baseGroup || !map) return;

  const resolution = map.getView().getResolution();
  const zoom = map.getView().getZoom?.();
  const layers = baseGroup.getLayers().getArray();

  // Layers enabled by rights/backend/user and within zoom range.
  const zoomVisibleLayers = layers.filter(layer => {
    return !layer.get('disabledByRights') &&
      layer.get('backendVisible') !== false &&
      layer.getVisible() &&
      isViewInLayerVisibilityRange(layer, resolution, zoom);
  });

  // CASE 1: zoom-valid → show legend
  if (zoomVisibleLayers.length > 0) {
    msgEl.innerHTML = zoomVisibleLayers.map(layer => {
      const title = layer.get('title') || 'Layer';
      const backendLegend = backendStyleToLegendHTML(layer.get('backendStyle'));
      const style =
        typeof layer.getStyle() === 'function'
          ? layer.getStyle()()
          : layer.getStyle();

      const legend = backendLegend || styleToLegendHTML(style);

      return `
        <span style="
          display:inline-flex;
          align-items:center;
          gap:6px;
          margin-right:12px;
          white-space:nowrap;
        ">
          ${legend}
          <span>${title}</span>
        </span>
      `;
    }).join('');
    return;
  }

  // CASE 2: enabled layers exist, but none belongs to the current zoom.
  // Do not list every loaded layer; that makes the legend disagree with the map.
  const enabledLayers = layers.filter(layer =>
    !layer.get('disabledByRights') &&
    layer.get('backendVisible') !== false &&
    layer.getVisible()
  );
  if (enabledLayers.length > 0) {
    msgEl.textContent = 'No base data visible at current zoom';
    return;
  }

  // CASE 3: nothing loaded
  msgEl.textContent = 'No base data visible';
}


